import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import {
  Box,
  Toolbar,
  List as MuiList,
  Typography,
  IconButton,
  Grid,
  Drawer,
  Avatar,
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { WinDims } from "../hooks/Method";

const List = styled(MuiList)({
  "&": {
    margin: "5px",
  },
  "& .MuiListItemButton-root": {
    borderRadius: "10px",
    marginBottom: "0px",
    "&, & .MuiListItemIcon-root": {
      color: "var(--gray-color)",
      minWidth: "40px",
    },
  },
  // hover states
  "& .MuiListItemButton-root:hover": {
    backgroundColor: "var(--primaryTSM-color)",
    "&, & .MuiListItemIcon-root": {
      color: "var(--darkGreen-color)",
    },
    "& .MuiListItemText-root .MuiTypography-root": {
      fontWeight: "600",
    },
  },
});

const drawerWidth = 280;

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  top: "inherit",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function Dashboard(props) {
  const {
    menu,
    header,
    children,
    title = "",
    subTitle = "",
    showNav = true,
    ...other
  } = props;
  const minWidth = 1200;
  const windowDims = WinDims().width;
  const [open, setOpen] = useState(
    showNav && (windowDims > minWidth ? true : false)
  );
  const toggleDrawer = () => {
    setOpen(!open);
  };

  useEffect(() => {
    showNav && setOpen(windowDims > minWidth ? true : false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDims]);

  return (
    <Box sx={{ display: "flex" }} {...other}>
      {/* --- Navbar --- */}
      <AppBar
        open={open}
        color="white"
        elevation={0}
        sx={{ borderBottom: "1px solid var(--deepWhite-color)" }}
      >
        <Toolbar className="pr-20">
          {showNav && (
            <IconButton
              color="dark"
              edge="start"
              className="mr-10"
              aria-label="open drawer"
              onClick={toggleDrawer}
            >
              <MenuRoundedIcon />
            </IconButton>
          )}
          {header}
        </Toolbar>
      </AppBar>
      {/* --- Menu --- */}
      <Drawer
        open={open}
        onClose={() => setOpen(!open)}
        variant="persistent"
        sx={{
          "& .MuiDrawer-paper": {
            display: open ? "block" : "none",
            width: "280px",
            position: { md: "relative", xs: "" },
            whiteSpace: "nowrap",
            boxSizing: "border-box",
            borderRight: "1px solid rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            px: [1],
            borderTopRightRadius: "0.8rem",
            borderBottomRightRadius: "0.8rem",
            backgroundColor: "var(--primaryTSM-color)",
            color: "var(--darkGreen-color)",
            margin: "20px 20px 20px 0px",
            padding: "8px 15px !important",
          }}
        >
          <Avatar alt="profile-pic" className="mr-20 bg-primary" />
          <Grid sx={{ lineHeight: "14pt" }}>
            <Typography variant="p" className="fs-18 fw-5">
              {title}
            </Typography>
            <Typography className="fs-14 d-grid">{subTitle}</Typography>
          </Grid>
        </Toolbar>
        <List
          sx={{
            height: "calc(100vh - 146px)",
            overflow: "auto",
            p: "5px",
          }}
        >
          {menu}
        </List>
      </Drawer>
      {/* --- Body --- */}
      <Box
        component="main"
        className="bg-default p-20 pt-90 w-100pr"
        sx={{
          overflow: "auto",
          height: "calc(100vh - 110px)",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
